import React, { useState, useCallback } from 'react';
import TRANSITION from '@integrations/pint-appstore-commons/src/enums/APP_STATUS_TRANSITION.json';
import ConfirmationModal from '../../common/ConfirmationModal';
import Icon from '../../common/Icon';
import { DesktopAppDefaults } from './DesktopAppDefaults';
import { CoEditingSettings, CollaborateSettings, Step } from './interfaces';
import { changeAppSettings, updateCoeditPolicy } from '../../common/appStatusActionCreators';
import { CollaborateDefaults } from './CollaborateDefaults';
import { TrustedLocation } from './TrustedLocation';
import { DeviceSetup } from './DeviceSetup';
import { getCoeditPolicyFromSelectValue } from './MSOfficeDesktopModalUtils';
import { AppId } from '../../common/interfaces';

export interface MSOfficeDesktopModalProps {
    name: string;
    onClose: () => void;
    onConfirm: () => void;
    dispatchChangeAppSettings: typeof changeAppSettings;
    appId: string;
    dispatchUpdateCoeditPolicy: typeof updateCoeditPolicy;
    collaborationAppsHierarchy?: AppId[];
}

export function MSOfficeDesktopModal({
    name,
    onClose,
    onConfirm,
    dispatchChangeAppSettings,
    appId,
    dispatchUpdateCoeditPolicy,
    collaborationAppsHierarchy,
}: MSOfficeDesktopModalProps) {
    const [currentStep, setCurrentStep] = useState<Step>(Step.LICENSE_CHECK);
    const [coEditingSettings, setCoEditingSettings] = useState<CoEditingSettings>({
        word: 'Start co-editing automatically',
        excel: 'Start co-editing automatically',
        powerPoint: 'Start co-editing automatically',
        allowOverwrite: false,
    });
    const [collaborateSettings, setCollaborateSettings] = useState<CollaborateSettings>({
        makeDefaultCoEditing: false,
    });

    // Render step indicator based on current step
    const renderStepIndicator = () => (
        <div className="step-indicator">
            {['App Setup', 'Collaborate Setup', 'Trusted Place', 'Device Setup'].map((stepName, index) => {
                const step = index + 2; // Adjusting index to match the step numbers
                let stepClass = 'next'; // default class
                if (currentStep > step) {
                    stepClass = 'completed';
                } else if (currentStep === step) {
                    stepClass = 'current';
                }

                return (
                    <div key={step} className={`step ${stepClass}`}>
                        <div className="step-circle">
                            {currentStep > step ? <Icon alias="check" /> : <Icon alias="card" />}
                        </div>
                        <div className="step-name">{stepName}</div>
                    </div>
                );
            })}
        </div>
    );

    // Render content for each step
    const renderLicenseCheck = () => (
        <div className="content">
            <p>
                Before enabling the application please make sure, that user who will be using it has at least any of the
                following:
            </p>
            <ul>
                <li>Microsoft 365 Business Standard license or above</li>
                <li>Enterprise Office 365 E3 subscription or above</li>
            </ul>
            <a
                href="https://helpdesk.egnyte.com/hc/en-us/articles/5980704498829-Configure-Co-Editing-for-Microsoft-Office-Desktop"
                className="learn-more-link"
                target="_blank"
                rel="noopener noreferrer"
            >
                Learn more
            </a>
        </div>
    );

    const renderDesktopAppDefaults = () => (
        <DesktopAppDefaults coEditingSettings={coEditingSettings} setCoEditingSettings={setCoEditingSettings} />
    );

    const renderCollaborateDefaults = () => (
        <CollaborateDefaults
            collaborateSettings={collaborateSettings}
            setCollaborateSettings={setCollaborateSettings}
            showPreview
        />
    );

    const renderTrustedLocation = () => <TrustedLocation />;

    const renderDeviceSetup = () => <DeviceSetup />;

    // Determine which content to render based on the current step
    const renderContent = () => {
        switch (currentStep) {
            case Step.LICENSE_CHECK:
                return renderLicenseCheck();
            case Step.DESKTOP_APP_DEFAULTS:
                return renderDesktopAppDefaults();
            case Step.COLLABORATE_DEFAULTS:
                return renderCollaborateDefaults();
            case Step.TRUSTED_LOCATION:
                return renderTrustedLocation();
            case Step.DEVICE_SETUP:
                return renderDeviceSetup();
            default:
                return null;
        }
    };

    // Handle navigation to the previous step
    const handleBack = useCallback(() => {
        if (currentStep > Step.LICENSE_CHECK) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);

    // Handle navigation to the next step or confirm changes
    const handleNext = useCallback(() => {
        if (currentStep < Step.DEVICE_SETUP) {
            setCurrentStep(currentStep + 1);
        } else {
            const appsHierarchy = collaborateSettings.makeDefaultCoEditing
                ? [appId, ...(collaborationAppsHierarchy?.filter((id) => id !== appId) || [])]
                : collaborationAppsHierarchy?.filter((id) => id !== appId) || [];
            Promise.resolve(
                dispatchChangeAppSettings(
                    {
                        appId,
                        status: { transition: TRANSITION.ADD, groups: [] },
                        editors: {
                            collaborationAppsHierarchy: appsHierarchy,
                            collaborationAppsPrioritizationEnabled: appsHierarchy.length > 0,
                        },
                    },
                    collaborateSettings.makeDefaultCoEditing
                )
            ).then(() => {
                Promise.resolve(
                    dispatchUpdateCoeditPolicy({
                        arePoliciesEnabled: true,
                        userCanOverwrite: coEditingSettings.allowOverwrite,
                        excel: getCoeditPolicy(coEditingSettings.excel),
                        word: getCoeditPolicy(coEditingSettings.word),
                        powerpoint: getCoeditPolicy(coEditingSettings.powerPoint),
                    })
                ).then(() => {
                    onConfirm();
                });
            });
        }
    }, [
        appId,
        collaborateSettings,
        coEditingSettings,
        currentStep,
        dispatchChangeAppSettings,
        dispatchUpdateCoeditPolicy,
        onConfirm,
    ]);

    // Map co-edit option to policy
    const getCoeditPolicy = useCallback(getCoeditPolicyFromSelectValue, []);

    // Determine button labels based on the current step
    const getButtonLabels = () => {
        if (currentStep === Step.LICENSE_CHECK) {
            return { confirm: 'Continue to setup', cancel: 'Cancel' };
        }
        if (currentStep === Step.DEVICE_SETUP) {
            return { confirm: 'Finish and Enable Integration', cancel: 'Back' };
        }

        return { confirm: 'Next', cancel: 'Back' };
    };

    const buttonLabels = getButtonLabels();

    const title = (
        <div className="ms-office-desktop-modal-header">
            <div className="ms-office-desktop-modal-title">{name}</div>
            {currentStep !== Step.LICENSE_CHECK && (
                <div className="ms-office-desktop-modal-subtitle">
                    Wizard will help you enable Microsoft Office Desktop Integration and guide how to setup users&apos;
                    devices
                </div>
            )}
        </div>
    );

    return (
        <ConfirmationModal
            className="ast-app-status-form ms-office-desktop-modal"
            title={title}
            confirmLabel={buttonLabels.confirm}
            cancelLabel={buttonLabels.cancel}
            confirmHandler={handleNext}
            closeHandler={onClose}
            cancelButtonHandler={currentStep === Step.LICENSE_CHECK ? onClose : handleBack}
            isDisabled={false}
            hideCloseButton={false}
            closeBtnClassName="ms-office-desktop-modal-close-btn"
            buttonWrapperClassName="dialog-actions"
        >
            {currentStep !== Step.LICENSE_CHECK && renderStepIndicator()}
            {renderContent()}
        </ConfirmationModal>
    );
}
